import React, { useState, useEffect } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { useLiffProfile } from "../../context/LiffProfileContext";
import LoadingBar from "../LoadingBar";
import DangerButton from "../common/DangerButton";
import bigPointIcon from "../../assets/images/coin-icon.png";
import LoginHeaderSuccess from "../header/LoginHeaderSuccess";
import { MaskInputHelper } from "../helpers/mask-input-helper";


const SuccessBindingForm = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const payload = location.state;

  // ถ้ามาจากเกม BTS → auto redirect กลับเกม + ลบ flag
  useEffect(() => {
    const redirectUrl = sessionStorage.getItem('bts_redirect_after_register');
    if (redirectUrl) {
      sessionStorage.removeItem('bts_redirect_after_register');
      const timer = setTimeout(() => {
        navigate(redirectUrl, { replace: true });
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [navigate]);
  const { t, i18n } = useTranslation();
  const { authToken, userId, profile } = useLiffProfile();
  const [data, setData] = useState({
    point: 0,
    totalGamePoint: 0,
    bigCardNumber: "",
    pointExpire: 0,
    missionGroups: [],
  });
  const [loading, setLoading] = useState(true);
  const [seniorLayout, setSeniorLayout] = useState(false);
  const [pointExpireData, setPointExpireData] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const clientAuth = process.env.REACT_APP_CLIENT_AUTH;
        const headers = {
          Authorization: `Bearer ${authToken}`,
          "X-Client-Auth": clientAuth,
          "x-user-id": userId,
        };

        const userResponse = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/users/current`,
          { headers }
        );
        const pointResponse = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/users/current/clm-point`,
          { headers }
        );

        const pointExpireResponse = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/users/current/clm-point-expire`,
          { headers }
        );

        setData({
          point: pointResponse.data?.data?.pointBalance || 0,
          totalGamePoint: userResponse.data?.data?.totalGamePoint || 0,
          bigCardNumber: userResponse.data?.data?.bigCardNumber || "",
          pointExpire: pointResponse.data?.data?.pointExpire,
          missionGroups: userResponse.data?.data?.missionGroups || [],
        });
        setPointExpireData(pointExpireResponse.data?.data);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [authToken, userId]);

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const options = { year: "numeric", month: "short", day: "numeric" };
    return new Date(dateString).toLocaleDateString(
      i18n.language || "en-US",
      options
    );
  };

  if (loading) return <LoadingBar />;

  // Reusable component for displaying point or game data
  const DataCard = ({ title, value, icon, footerText }) => (
    <div className="w-full h-full p-2 flex flex-col justify-between bg-secondary-text-invert rounded-2xl bg-white">
      <div className="w-full flex items-center text-primary-text gap-1.5">
        <img src={icon} alt="Point Icon" className="w-8 h-8" />
        <div>{title}</div>
      </div>
      <div className="w-full pb-2 pr-3 xs:pr-1 text-32 font-semibold text-right">
        {value ?? "0"}
      </div>
      {footerText && <div>{footerText}</div>}
    </div>
  );

  return (
    <div>
      <LoginHeaderSuccess />
      <div className="w-full min-h-screen bg-secondary-background-gray">
        <div className="px-10 sm:px-4 py-12 sm:py-8 xs:py-4">
          <div className="relative w-full">
            <div className="w-full h-auto transition-opacity duration-150 ease-in-out opacity-100">
            <div className="w-full h-full flex flex-col px-2 items-center text-center">
                <div className="w-full text-3xl font-semibold text-primary-text">
                  {t("welcome")}
                </div>
                <div
                  className={`w-full font-medium ${
                    seniorLayout ? "text-base" : "text-lg"
                  }`}
                >
                  {t("binding_success")}
                </div>
                <div className="w-full h-full flex flex-col items-center text-center gap-2">
                  <img
                    className="w-36 h-auto my-2 drop-shadow-xl rounded-full border-4 border-secondary-background"
                    src={profile?.pictureUrl}
                    alt="Profile"
                  />
                  <div className="w-full text-[22px] font-medium">
                    {profile?.displayName}
                  </div>
                  <div className="w-full h-12 text-base font-medium">
                    <div>{t("big_point_number")}</div>
                    <div>{data.bigCardNumber}</div>
                  </div>
                  {/* <Points point={data.point} gamePoint={data.totalGamePoint} /> */}
                  {/* <PointExpire pointExpire={data.pointExpire} /> */}
                  <div className="w-full h-22 my-2 grid grid-cols-2 font-medium text-base gap-2 ">
                    <DataCard
                      title={t("my_big_point")}
                      value={MaskInputHelper.formatNumber(data?.point|| 0)}
                      icon={bigPointIcon}
                    />
                    <DataCard
                      title={t("big_game_gems")}
                      value={data.totalGamePoint}
                      icon={bigPointIcon}
                    />
                  </div>
                  {/* Active Missions */}
                  {data.missionGroups.length > 0 && (
                    <div className="w-full py-8">
                      <div className="font-semibold text-2xl">
                        {t("active_missions")}
                      </div>
                      <div className="w-full flex flex-col gap-4 py-2">
                        <h3>{t("mission_card")}</h3>
                      </div>
                    </div>
                  )}
                </div>
                {/* Mission Status */}
                <div className="w-full border-l-[0.1875rem] border-[#da0010] my-2 flex items-center gap-4 text-base font-medium pl-4">
                  <div className="text-left">
                    <div className="text-base text-danger">
                      <span className="text-32"></span>
                      <span>{MaskInputHelper.formatNumber(pointExpireData?.pointAmount ?? 0)}</span>
                      <span> {t("point")} </span>
                      <span>
                        {t("expired")} {t("day")}{" "}
                        {pointExpireData
                          ? formatDate(pointExpireData.expireDate)
                          : ""}
                      </span>
                    </div>
                  </div>
                </div>

                <div className={`w-48 h-13 ${seniorLayout ? "mt-3" : "mt-8"}`}>
                  <Link to="/">
                    <DangerButton buttonName={t("go_to_home")} />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SuccessBindingForm;
